// libs/ui/src/primitives/Text.tsx
import type { ComponentProps } from 'react';
import { styled, Text as TamaguiText } from 'tamagui';
import { typeScale } from '../theme/typography';

const StyledText = styled(TamaguiText, {
  name: 'Text',
  color: '$textOnSurface',

  variants: {
    // Variants pull lineHeight from typeScale (ADR-0073) so visual
    // rhythm is identical across iOS, Android, and web.
    variant: {
      heading: typeScale.heading,
      subheading: typeScale.subheading,
      title: typeScale.title,
      body: typeScale.body,
      caption: { ...typeScale.caption, color: '$textSubdued' },
      label: typeScale.label,
      overline: {
        ...typeScale.overline,
        color: '$textSubdued',
        textTransform: 'uppercase',
      },
    },
  } as const,

  defaultVariants: {
    variant: 'body',
  },
});

type StyledTextProps = ComponentProps<typeof StyledText>;

export type TextRole = 'heading' | 'label' | 'paragraph';

const ROLE_TO_A11Y: Record<TextRole, 'header' | 'text'> = {
  // Native accessibilityRole accepts a small set — 'header' is the
  // closest mapping for headings. label/paragraph both announce as
  // plain 'text'. On web, RN-web reflects accessibilityRole=header into
  // an <h*> element automatically, so we don't override the tag.
  // ADR-0074.
  heading: 'header',
  label: 'text',
  paragraph: 'text',
};

export interface TextStateProps {
  /**
   * Semantic role — `heading` announces as a header (and RN-web
   * reflects to `<h*>`), `label` / `paragraph` announce as plain text.
   * Use for standalone copy where the surrounding context doesn't
   * already supply a heading or label. Optional — most text is
   * decorative inside a pre-labelled surface.
   */
  role?: TextRole;
}

export type TextProps = StyledTextProps & TextStateProps;

/**
 * Typographic primitive. Variants pull `lineHeight` from `typeScale`
 * (ADR-0073) so paragraphs read consistently across platforms. Pass
 * `role` to announce the right `accessibilityRole` (RN-web reflects
 * `header` into a semantic `<h*>` element automatically).
 */
export function Text({ role, ...rest }: TextProps) {
  if (!role) return <StyledText {...rest} />;
  return <StyledText {...rest} accessibilityRole={ROLE_TO_A11Y[role]} />;
}
